import { useEffect, useRef, useState } from "react";
import {
  MessageSquareText, Plus, Pencil, Trash2, Search, Loader2, Settings2, Hash,
} from "lucide-react";
import { Button, Input, Label, Modal, ModalFooter, Textarea, useConfirm, useToast } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * A canned response / snippet. `body` is stored verbatim — it may contain
 * merge tokens like {{first_name}} that are resolved at send time, so the
 * composer inserts the raw text and never tries to interpolate it here.
 */
export interface Snippet {
  id: string;
  title: string;
  shortcut: string | null;
  body: string;
  /** email | sms | null (null = available on every channel) */
  channel: string | null;
  usage_count: number;
}

type Channel = "email" | "sms";

/**
 * Reusable canned-responses dropdown. Renders its own trigger button; the
 * host just supplies the channel to scope by and an `onInsert` callback that
 * receives the chosen snippet's raw body (tokens included, unresolved).
 *
 * Clicking a snippet fires POST /snippets/:id/use fire-and-forget so usage
 * counts stay warm without blocking the insert. A "Manage" affordance opens
 * an inline CRUD modal.
 */
export function SnippetPicker({
  channel,
  onInsert,
  className,
}: {
  channel?: Channel;
  onInsert: (body: string) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [snippets, setSnippets] = useState<Snippet[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const toast = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.get<{ snippets: Snippet[] }>("/snippets", { channel });
      setSnippets(d.snippets ?? []);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to load snippets");
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  // Fetch the first time the dropdown opens; refetches happen after the
  // manage modal saves changes (see onChanged).
  useEffect(() => {
    if (open && !loaded) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loaded]);

  // Dismiss the dropdown on outside-click / Escape — same pattern as the
  // mail snooze popover. The manage modal handles its own dismissal.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open]);

  const insert = (s: Snippet) => {
    onInsert(s.body);
    // Fire-and-forget usage bump — never blocks or surfaces errors.
    api.post(`/snippets/${s.id}/use`).catch(() => {});
    setSnippets((prev) => prev.map((x) => x.id === s.id ? { ...x, usage_count: x.usage_count + 1 } : x));
    setOpen(false);
    setQuery("");
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? snippets.filter((s) =>
        s.title.toLowerCase().includes(q) ||
        (s.shortcut ?? "").toLowerCase().includes(q) ||
        s.body.toLowerCase().includes(q))
    : snippets;

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border text-xs font-medium transition-colors",
          open
            ? "border-rose-500/50 bg-rose-500/15 text-rose-200"
            : "border-zinc-800 bg-zinc-900/50 text-zinc-300 hover:text-zinc-100 hover:border-zinc-700"
        )}
        aria-label="Insert a canned response"
      >
        <MessageSquareText className="h-3.5 w-3.5" />
        Snippets
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-50 w-80 rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl shadow-shade/60 animate-fade-in overflow-hidden">
          <div className="p-2 border-b border-zinc-900">
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search canned responses…"
                className="w-full pl-7 pr-2 h-8 rounded-lg bg-zinc-900 border border-zinc-800 text-xs text-zinc-100 placeholder:text-zinc-500 focus:border-rose-500/50 focus:outline-none"
              />
            </div>
          </div>

          <div className="max-h-72 overflow-y-auto py-1">
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-xs text-zinc-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-zinc-500">
                {snippets.length === 0
                  ? "No canned responses yet. Create one to reuse it here."
                  : "No snippets match your search."}
              </div>
            ) : (
              filtered.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => insert(s)}
                  className="w-full text-left px-3 py-2 hover:bg-zinc-900 transition-colors group"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-zinc-100 truncate flex-1">{s.title}</span>
                    {s.shortcut && (
                      <span className="inline-flex items-center gap-0.5 text-[10px] text-zinc-500 shrink-0">
                        <Hash className="h-2.5 w-2.5" />{s.shortcut}
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-[11px] text-zinc-500 line-clamp-2">{s.body}</p>
                </button>
              ))
            )}
          </div>

          <div className="border-t border-zinc-900 p-1.5">
            <button
              type="button"
              onClick={() => { setManageOpen(true); setOpen(false); }}
              className="w-full flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100 transition-colors"
            >
              <Settings2 className="h-3.5 w-3.5 text-zinc-500" />
              Manage snippets
            </button>
          </div>
        </div>
      )}

      {manageOpen && (
        <SnippetManagerModal
          defaultChannel={channel}
          onClose={() => setManageOpen(false)}
          onChanged={() => { setLoaded(false); if (open) void load(); }}
        />
      )}
    </div>
  );
}

// ─── Manage modal ────────────────────────────────────────────────────────────
// Full CRUD in one place: the list on the left of the create/edit form. Kept
// deliberately compact so it reads like the rest of the app's dialogs.

function SnippetManagerModal({
  defaultChannel,
  onClose,
  onChanged,
}: {
  defaultChannel?: Channel;
  onClose: () => void;
  onChanged: () => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();

  const [snippets, setSnippets] = useState<Snippet[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Snippet | "new">("new");

  const load = async () => {
    setLoading(true);
    try {
      // No channel filter here — the manager shows every snippet regardless
      // of which channel it's scoped to.
      const d = await api.get<{ snippets: Snippet[] }>("/snippets");
      setSnippets(d.snippets ?? []);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to load snippets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const onDelete = async (s: Snippet) => {
    if (!(await confirm({
      title: `Delete "${s.title}"?`,
      confirmLabel: "Delete snippet",
      danger: true,
    }))) return;
    try {
      await api.delete(`/snippets/${s.id}`);
      setSnippets((prev) => prev.filter((x) => x.id !== s.id));
      if (editing !== "new" && editing.id === s.id) setEditing("new");
      onChanged();
      toast.success("Snippet deleted");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to delete");
    }
  };

  return (
    <Modal open onClose={onClose} title="Canned responses" size="lg">
      <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,11rem)_1fr] gap-4 min-h-[22rem]">
        {/* List rail */}
        <div className="flex flex-col gap-1.5 sm:border-r sm:border-zinc-800 sm:pr-4">
          <button
            type="button"
            onClick={() => setEditing("new")}
            className={cn(
              "flex items-center gap-2 rounded-lg px-2.5 py-2 text-xs font-medium transition-colors",
              editing === "new"
                ? "bg-rose-500/15 text-rose-200"
                : "text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100"
            )}
          >
            <Plus className="h-3.5 w-3.5" /> New snippet
          </button>
          <div className="flex-1 overflow-y-auto -mr-1 pr-1 max-h-72 space-y-0.5">
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-xs text-zinc-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
              </div>
            ) : snippets.length === 0 ? (
              <p className="px-2.5 py-4 text-[11px] text-zinc-500">No snippets yet.</p>
            ) : (
              snippets.map((s) => {
                const active = editing !== "new" && editing.id === s.id;
                return (
                  <div
                    key={s.id}
                    className={cn(
                      "group flex items-center gap-1 rounded-lg pl-2.5 pr-1 py-1.5 transition-colors",
                      active ? "bg-zinc-800/70" : "hover:bg-zinc-900"
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => setEditing(s)}
                      className="flex-1 min-w-0 text-left"
                    >
                      <span className="block text-xs text-zinc-100 truncate">{s.title}</span>
                      {s.shortcut && (
                        <span className="block text-[10px] text-zinc-500 truncate">#{s.shortcut}</span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => void onDelete(s)}
                      className="h-6 w-6 grid place-items-center rounded text-zinc-600 hover:text-rose-300 hover:bg-rose-500/10 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label={`Delete ${s.title}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Editor form */}
        <SnippetForm
          key={editing === "new" ? "new" : editing.id}
          target={editing}
          defaultChannel={defaultChannel}
          onSaved={(saved, mode) => {
            setSnippets((prev) => mode === "create"
              ? [saved, ...prev]
              : prev.map((x) => x.id === saved.id ? saved : x));
            setEditing(saved);
            onChanged();
          }}
        />
      </div>
    </Modal>
  );
}

// ─── Create / edit form ──────────────────────────────────────────────────────
// Remounted (via `key`) whenever the selected target changes, so the local
// field state always initialises from the right row without a sync effect.

function SnippetForm({
  target,
  defaultChannel,
  onSaved,
}: {
  target: Snippet | "new";
  defaultChannel?: Channel;
  onSaved: (snippet: Snippet, mode: "create" | "edit") => void;
}) {
  const toast = useToast();
  const editing = target === "new" ? null : target;

  const [title, setTitle] = useState(editing?.title ?? "");
  const [shortcut, setShortcut] = useState(editing?.shortcut ?? "");
  const [body, setBody] = useState(editing?.body ?? "");
  const [channel, setChannel] = useState<string>(editing?.channel ?? defaultChannel ?? "");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!title.trim() || !body.trim()) return;
    setSaving(true);
    const payload = {
      title: title.trim(),
      shortcut: shortcut.trim() || null,
      body,
      channel: channel || null,
    };
    try {
      if (editing) {
        const r = await api.patch<{ snippet: Snippet }>(`/snippets/${editing.id}`, payload);
        onSaved(r.snippet, "edit");
        toast.success("Snippet updated");
      } else {
        const r = await api.post<{ snippet: Snippet }>("/snippets", payload);
        onSaved(r.snippet, "create");
        toast.success("Snippet created");
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="space-y-3 flex-1">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_9rem] gap-3">
          <div>
            <Label required>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Welcome reply" autoFocus />
          </div>
          <div>
            <Label>Shortcut</Label>
            <Input
              value={shortcut}
              onChange={(e) => setShortcut(e.target.value)}
              leftIcon={<Hash className="h-3.5 w-3.5" />}
              placeholder="welcome"
            />
          </div>
        </div>
        <div>
          <Label required>Body</Label>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Hi {{first_name}}, thanks for reaching out…"
            className="min-h-[9rem] text-xs"
          />
          <p className="mt-1.5 text-[11px] text-zinc-500">
            Use tokens like <code className="text-zinc-300">{"{{first_name}}"}</code> — they're filled in when the message is sent.
          </p>
        </div>
        <div>
          <Label>Channel</Label>
          <select
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            className="w-full h-9 px-3 rounded-lg bg-zinc-900 border border-zinc-800 text-sm text-zinc-100 focus:border-rose-500/50 focus:outline-none"
          >
            <option value="">All channels</option>
            <option value="email">Email</option>
            <option value="sms">SMS</option>
          </select>
        </div>
      </div>
      <ModalFooter>
        <Button onClick={() => void submit()} loading={saving} disabled={!title.trim() || !body.trim()}>
          {editing
            ? <><Pencil className="h-3.5 w-3.5 mr-1.5" />Save changes</>
            : <><Plus className="h-3.5 w-3.5 mr-1.5" />Create snippet</>}
        </Button>
      </ModalFooter>
    </div>
  );
}
